import React, { FC, useState, useEffect } from "react";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { StripeCardElement } from "@stripe/stripe-js";
import firebase from "firebase/app";
import { PaymentInfoType } from "../Account/AccountBilling/PaymentMethod";
import { FlexGroup } from "../Grid";
import { Spinner, LabelWrapper, StripeInput, Form, Button } from "../System";
import { StyledUpdateCardInfo } from ".";
import { useNotifications } from "../Notifications";

import MasterCard from "../icons/mastercard-logo.svg";
import Visa from "../icons/visa.svg";
import Discover from "../icons/discover.svg";
import AmericanExpress from "../icons/american-express.svg";
import JCB from "../icons/jcb.svg";

type Props = {
  onCreditCardChange: (value: boolean) => void;
};

const UpdateCardInfo: FC<Props> = ({ onCreditCardChange }) => {
  const stripe = useStripe();
  const elements = useElements();
  const attachPaymentMethod = firebase
    .functions()
    .httpsCallable("attachPaymentMethod");
  const getPaymentInfo = firebase.functions().httpsCallable("getPaymentInfo");
  const { addNotification } = useNotifications();

  const [cardError, setCardError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [cardInfo, setCardInfo] = useState<
    PaymentInfoType | undefined | null
  >();
  const [errorText, setErrorText] = useState("");
  const [edit, setEdit] = useState(false);

  useEffect(() => {
    getCardInfo();
  }, []);

  useEffect(() => {
    onCreditCardChange(!!cardInfo);
    setEdit(!cardInfo);
  }, [cardInfo]);

  const returnCardIcon = (cardName: string) => {
    switch (cardName) {
      case "visa":
        return Visa;
      case "mastercard":
        return MasterCard;
      case "amex":
        return AmericanExpress;
      case "jcb":
        return JCB;
      case "discover":
        return Discover;
      default:
        return Visa;
    }
  };

  const getCardInfo = async () => {
    try {
      const card = await getPaymentInfo({});

      if (!card.data?.card) {
        setEdit(true);
        setCardInfo(null);
        return;
      }

      setCardInfo({
        cardName: card.data.card.brand,
        last4: card.data.card.last4,
        expMonth: card.data.card.exp_month.toString(),
        expYear: card.data.card.exp_year.toString().slice(2),
      });
    } catch (err) {
      setErrorText(err.message);
    }
  };

  const handleChange = (e) => {
    setCardError("");
    setIsComplete(e.complete);

    if (e.error?.message) {
      setCardError(e.error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    if (!stripe || !elements) {
      setIsProcessing(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement as StripeCardElement,
    });

    if (error) {
      setCardError(error.message as string);
      setIsProcessing(false);
      return;
    }

    const method = await attachPaymentMethod({
      paymentMethodId: paymentMethod?.id,
    }).catch((err) => {
      setIsProcessing(false);
      setCardError(err.message);
    });

    if (!method) {
      setIsProcessing(false);
      return;
    }

    await getCardInfo();

    addNotification({
      message: "Payment method saved!",
      type: "success",
      interval: 5000,
    });
    setEdit(false);
    setIsProcessing(false);
  };

  if (edit) {
    return (
      <StyledUpdateCardInfo edit isProcessing={isProcessing}>
        <Form onSubmit={(e) => handleSubmit(e)}>
          <div className="card-info-header">
            <FlexGroup>
              <h2>Card Information</h2>
            </FlexGroup>
            <FlexGroup className="button-container">
              {isProcessing && <p className="loading">Loading</p>}
              {!isProcessing && (
                <>
                  <button
                    className="cancel"
                    onClick={(e) => {
                      e.preventDefault();
                      if (cardInfo) {
                        setEdit(false);
                      }
                    }}
                  >
                    Cancel
                  </button>
                  <Button
                    isDisabled={!isComplete || cardError || isProcessing}
                    isProcessing={isProcessing}
                    action="submit"
                    className="save"
                  >
                    Save
                  </Button>
                </>
              )}
            </FlexGroup>
          </div>
          <div className="card-info-container">
            {isProcessing && <Spinner />}
            <div className="card-input-wrap">
              <p className="edit-view-header">Update Your Card</p>
              <LabelWrapper errorMessage={cardError}>
                <StripeInput
                  onChange={(e) => handleChange(e)}
                  isDisabled={isProcessing}
                />
              </LabelWrapper>
            </div>
          </div>
        </Form>
      </StyledUpdateCardInfo>
    );
  }

  return (
    <StyledUpdateCardInfo>
      <h2>Card Information</h2>
      {!cardInfo && <Spinner />}
      {cardInfo && (
        <div className="card-info-container">
          {errorText && <p>{errorText}</p>}
          {cardInfo !== null && (
            <>
              <div className="card-info">
                <FlexGroup>
                  <div className="card-logo">
                    <img
                      src={returnCardIcon(cardInfo.cardName)}
                      alt="credit card logo"
                    />
                  </div>
                  <p className="card-name">
                    {cardInfo?.cardName} **** {cardInfo?.last4}
                  </p>
                </FlexGroup>
                <FlexGroup>
                  <button
                    className="edit"
                    onClick={() => {
                      setEdit(true);
                    }}
                  >
                    Edit
                  </button>
                </FlexGroup>
              </div>
            </>
          )}
        </div>
      )}
    </StyledUpdateCardInfo>
  );
};

export default UpdateCardInfo;
